import React, { useEffect, useState } from 'react';
import { Rol, Usuari, UsuariForm } from '../services/api';

interface UsuariModalProps {
  isOpen: boolean;
  usuari: Usuari | null;
  rols: Rol[];
  initialRolIds?: number[];
  onClose: () => void;
  onSave: (data: UsuariForm) => void;
}

function UsuariModal({ isOpen, usuari, rols, initialRolIds, onClose, onSave }: UsuariModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nom, setNom] = useState('');
  const [cognoms, setCognoms] = useState('');
  const [telefon, setTelefon] = useState('');
  const [actiu, setActiu] = useState(true);
  const [rolIds, setRolIds] = useState<number[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    if (usuari) {
      setEmail(usuari.email);
      setPassword('');
      setNom(usuari.nom);
      setCognoms(usuari.cognoms);
      setTelefon(usuari.telefon || '');
      setActiu(usuari.actiu);
      setRolIds(usuari.rols.map((rol) => rol.id));
    } else {
      setEmail('');
      setPassword('');
      setNom('');
      setCognoms('');
      setTelefon('');
      setActiu(true);
      setRolIds(initialRolIds || []);
    }

    setError('');
  }, [isOpen, usuari, initialRolIds]);

  const toggleRol = (rolId: number) => {
    setRolIds((current) =>
      current.includes(rolId)
        ? current.filter((id) => id !== rolId)
        : [...current, rolId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !nom.trim() || !cognoms.trim()) {
      setError('Email, nom i cognoms son obligatoris');
      return;
    }

    if (!usuari && !password.trim()) {
      setError('La contrasenya es obligatoria per crear un usuari');
      return;
    }

    if (rolIds.length === 0) {
      setError('Selecciona com a minim un rol');
      return;
    }

    onSave({
      email: email.trim(),
      password: password.trim() || undefined,
      nom: nom.trim(),
      cognoms: cognoms.trim(),
      telefon: telefon.trim(),
      actiu,
      rol_ids: rolIds,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{usuari ? 'Editar usuari' : 'Nou usuari'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="usuari-email">Email</label>
              <input
                id="usuari-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nom@centre.cat"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="usuari-telefon">Telefon</label>
              <input
                id="usuari-telefon"
                type="text"
                value={telefon}
                onChange={(e) => setTelefon(e.target.value)}
                placeholder="Opcional"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="usuari-nom">Nom</label>
              <input
                id="usuari-nom"
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="usuari-cognoms">Cognoms</label>
              <input
                id="usuari-cognoms"
                type="text"
                value={cognoms}
                onChange={(e) => setCognoms(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="usuari-password">
              {usuari ? 'Nova contrasenya' : 'Contrasenya'}
            </label>
            <input
              id="usuari-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={usuari ? 'Deixa-ho buit per mantenir-la' : 'Contrasenya inicial'}
            />
          </div>

          <div className="form-group">
            <label>Rols</label>
            <div className="role-grid">
              {rols.map((rol) => (
                <label key={rol.id} className="role-option">
                  <input
                    type="checkbox"
                    checked={rolIds.includes(rol.id)}
                    onChange={() => toggleRol(rol.id)}
                  />
                  <span>
                    <strong>{rol.nom}</strong>
                    <small>{rol.categoria === 'perfil' ? 'Perfil' : 'Carrec'} - {rol.ambit}</small>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={actiu}
                onChange={(e) => setActiu(e.target.checked)}
              />
              Usuari actiu
            </label>
          </div>

          {error && <p className="form-error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {usuari ? 'Guardar canvis' : 'Crear usuari'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UsuariModal;
